import { jsx as _jsx } from "react/jsx-runtime";
import { Permissions, ShortUrl } from "@air/api";
import { ShortUrlObjectType } from "@air/api/types";
import isArray from "lodash/isArray";
import { useRouter } from "next/router";
import { useCallback, useState } from "react";
import { PublicBoard } from "~/components/Layouts/PublicBoardLayout/PublicBoard";
import { PublicClip } from "~/components/Layouts/PublicClipLayout/PublicClip";
import { NotFound } from "~/components/NotFound/NotFound";
import { PasswordPage } from "~/components/PasswordPage/PasswordPage";
import { PublicBoardPage } from "~/components/v2/components/board/PublicBoardPage";
import { useShowUI2Page } from "~/components/v2/hooks/useShowUI2Page";
import { getPublicBoardLayoutWithProviders } from "~/components/v2/layouts/PublicBoardLayout";
import { createShortUrlBoardRoute, createShortUrlClipInBoardRoute } from "~/constants/routes";
import { fetchShortBoardData } from "~/swr-hooks/shortUrl/utils";
import { getBoardIdFromPath, getClipIdFromPath } from "~/utils/PathUtils";
const isPasscodeProtected = (props)=>!!props.passcodeRequired;
const isBoardRoute = (props)=>{
    var _props_shortId;
    return !!((_props_shortId = props.shortId) === null || _props_shortId === void 0 ? void 0 : _props_shortId.startsWith("b"));
};
const isClipRoute = (props)=>{
    var _props_shortId;
    return !!((_props_shortId = props.shortId) === null || _props_shortId === void 0 ? void 0 : _props_shortId.startsWith("c"));
};
export var __N_SSG = true;
/** staticProps are props that come in once from the values from getStaticProps down below. They never change after the initial render */ export default function ShortUrlPage(staticProps) {
    const { replace, query } = useRouter();
    const [verified, setVerified] = useState(!isPasscodeProtected(staticProps));
    const [initialBoardData, setInitialBoardData] = useState(isBoardRoute(staticProps) ? staticProps.initialData : undefined);
    const [initialClipData, setInitialClipData] = useState(isClipRoute(staticProps) ? staticProps.initialData : undefined);
    const { showUI2 } = useShowUI2Page();
    const onVerified = useCallback(async ()=>{
        if (isPasscodeProtected(staticProps)) {
            const shortId = staticProps.shortId;
            const res = await ShortUrl.getByShortId(shortId);
            switch(res.type){
                case ShortUrlObjectType.board:
                    {
                        if (isArray(query.slug)) {
                            const path = query.slug.join("/");
                            // boardId will be empty if user opened /a/${shortId}
                            const boardId = getBoardIdFromPath(path) || res.data.board.id;
                            const clipId = getClipIdFromPath(path);
                            const { board, workspace, permissions, requireAccount } = await fetchShortBoardData(shortId, boardId);
                            if (clipId) {
                                await replace(createShortUrlClipInBoardRoute(shortId, boardId, clipId));
                            } else {
                                await replace(createShortUrlBoardRoute(shortId, boardId));
                            }
                            setInitialBoardData({
                                board,
                                workspace,
                                permissions,
                                requireAccount
                            });
                        } else {
                            const boardId = res.data.board.id;
                            const { board, workspace, permissions, requireAccount } = await fetchShortBoardData(shortId, boardId);
                            await replace(createShortUrlBoardRoute(shortId, boardId));
                            setInitialBoardData({
                                board,
                                workspace,
                                permissions,
                                requireAccount
                            });
                        }
                        setVerified(true);
                    }
                    break;
                case ShortUrlObjectType.clip:
                    {
                        const permissions = await Permissions.getFromObjects({
                            shortId,
                            objects: {
                                clipIds: [
                                    res.data.id
                                ]
                            }
                        });
                        setInitialClipData({
                            clip: res.data,
                            permissions,
                            requireAccount: res.requireAccount
                        });
                        setVerified(true);
                    }
                    break;
                default:
                    throw new Error("Unexpected ShortUrlObject type");
            }
        }
    }, [
        query.slug,
        replace,
        staticProps
    ]);
    if (isPasscodeProtected(staticProps) && !verified) {
        /**
     * After the user verifies the password, the verified state change causes a re-render here
     * and the shortID is passed into the corresponding layouts below where their data is loaded client side
     */ return /*#__PURE__*/ _jsx(PasswordPage, {
            shortId: staticProps.shortId,
            onVerified: onVerified
        });
    } else if (isBoardRoute(staticProps) && initialBoardData) {
        return showUI2 ? getPublicBoardLayoutWithProviders(/*#__PURE__*/ _jsx(PublicBoardPage, {}), initialBoardData, staticProps.shortId) : /*#__PURE__*/ _jsx(PublicBoard, {
            ...staticProps,
            initialData: initialBoardData
        });
    } else if (isClipRoute(staticProps) && initialClipData) {
        return /*#__PURE__*/ _jsx(PublicClip, {
            ...staticProps,
            initialData: initialClipData
        });
    } else {
        return /*#__PURE__*/ _jsx(NotFound, {});
    }
}
